import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { CalculationMethod, DiscountKind, DocumentItemModel, InventoryDocumentKind, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { TooLargePercentageDiscountValueError, TooLargeQuotaDiscountValueError } from '../violations/discount-value-error';
import { TooSmallPercentageDiscountValueWarning, TooSmallQuotaDiscountValueWarning } from '../violations/discount-value-warning';
import { DocumentUnitIsRequired } from '../violations/document-unit-is-required-error';


export interface DocumentItemsValidationsBoPartModelPart extends ModelBase {
    calculationMethod: CalculationMethod;
    issuedByVatPayer: boolean;
    kind: SaleDocumentKind | PurchaseDocumentKind | InventoryDocumentKind;
}

export class DocumentItemsValidationsBoPart<TModelPart extends ModelBase & DocumentItemsValidationsBoPartModelPart> extends BusinessObjectPart<TModelPart> {
    public constructor(
        weaver: Weaver,
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<TModelPart>): Promise<void> {
        await super.initializeAsync(context);

        this.registerSymbolReadonlyRules();
        this.registerReadonlyOmitRules();
        this.registerDiscountValueValidationRules();
        this.registerUnitValidationRules();
    }

    protected registerSymbolReadonlyRules(): void {
        this.engine.readonly.addRule(
            [[DocumentItemModel, 'productId'], [DocumentItemModel, 'symbol']],
            [[DocumentItemModel, 'symbol']],
            ctx => !!ctx.source.productId
        );
    }

    protected registerReadonlyOmitRules(): void {
        this.engine.readonlyChecker.addOmitRule([
            [DocumentItemModel, 'value_vatAfterDiscount'],
            [DocumentItemModel, 'value_vatBeforeDiscount'],
            [DocumentItemModel, 'productId']
        ]);
    }

    private registerUnitValidationRules(): void {
        this.engine.validation.addRule(
            [[DocumentItemModel, 'unitId']],
            ctx => {
                if (this.bo.model.kind === PurchaseDocumentKind.EPurchaseInvoice) return true;
                return !!ctx.source.unitId;
            },
            DocumentUnitIsRequired
        );
    }

    private registerDiscountValueValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['calculationMethod'])(
            () => this.engine.validation.queueValidation([DocumentItemModel, 'discountValue'])
        )));

        this.engine.validation.addRule(
            [[DocumentItemModel, 'discountValue'], [DocumentItemModel, 'discountKind']],
            [[DocumentItemModel, 'discountValue']],
            ctx => {
                if (ctx.source.discountKind !== DiscountKind.Percentage)
                    return true;
                return ctx.source.discountValue <= 1;
            },
            TooLargePercentageDiscountValueError
        );

        this.engine.validation.addRule(
            [[DocumentItemModel, 'discountValue'], [DocumentItemModel, 'discountKind']],
            [[DocumentItemModel, 'discountValue']],
            ctx => {
                if (ctx.source.discountKind !== DiscountKind.Percentage)
                    return true;
                return ctx.source.discountValue >= 0;
            },
            TooSmallPercentageDiscountValueWarning
        );

        this.engine.validation.addRule(
            [[DocumentItemModel, 'discountValue'], [DocumentItemModel, 'discountKind'], [DocumentItemModel, 'price_netBeforeDiscount'], [DocumentItemModel, 'price_grossBeforeDiscount']],
            [[DocumentItemModel, 'discountValue']],
            ctx => {
                if (ctx.source.discountKind !== DiscountKind.Quota)
                    return true;
                const calculationMethod = this.bo.model.calculationMethod;
                if (calculationMethod === CalculationMethod.Manual) return true;
                const discount = ctx.source.discountValue;
                const netPrice = ctx.source.price_netBeforeDiscount;
                const grossPrice = ctx.source.price_grossBeforeDiscount;
                return (calculationMethod === CalculationMethod.Gross && grossPrice >= discount)
                    || (calculationMethod === CalculationMethod.Margin && grossPrice >= discount)
                    || (calculationMethod === CalculationMethod.Net && netPrice >= discount);
            },
            TooLargeQuotaDiscountValueError
        );

        this.engine.validation.addRule(
            [[DocumentItemModel, 'discountValue'], [DocumentItemModel, 'discountKind']],
            [[DocumentItemModel, 'discountValue']],
            ctx => {
                if (ctx.source.discountKind !== DiscountKind.Quota)
                    return true;
                return ctx.source.discountValue >= 0;
            },
            TooSmallQuotaDiscountValueWarning
        );
    }
}
